/**
 * Lazy RxJS 6 loader.
 *
 * Legacy Angular MFEs receive shell-provided globals like
 * `EY_RAPToR_getRumToken(...)` and immediately call `.pipe(map(...))` on the
 * result. The operators come from the MFE's own bundled `rxjs/operators` and
 * internally call `source.lift(...)` — which only exists on a real RxJS
 * `Observable`. A hand-rolled shim won't satisfy that contract.
 *
 * Rather than ship RxJS in the host bundle (the host doesn't use it), we
 * lazy-load the same-origin RxJS UMD bundle the first time a legacy MFE asks
 * for an Observable-returning global. Same pattern as `zone-loader.ts`.
 *
 * Resolution order:
 *   1. `import.meta.env.VITE_RXJS_URL` (full URL, lets ops override).
 *   2. same-origin public asset, version pinned to the same line MFEs share.
 */
const DEFAULT_URL = '/legacy-vendor/rxjs.umd.min.js';

interface RxJsSubscription {
  unsubscribe(): void;
}

interface RxJsObservable {
  pipe(...operators: unknown[]): RxJsObservable;
  subscribe(...args: unknown[]): RxJsSubscription;
  lift?(operator: unknown): RxJsObservable;
}

export interface RxJsRuntime {
  of<T>(...values: T[]): RxJsObservable;
  from<T>(input: Promise<T> | Iterable<T> | RxJsObservable): RxJsObservable;
  throwError(err: unknown): RxJsObservable;
}

declare global {
  interface Window {
    rxjs?: RxJsRuntime;
  }
}

let rxjsPromise: Promise<RxJsRuntime> | null = null;

export function ensureRxJs(): Promise<RxJsRuntime> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('RxJS shim requires a browser window'));
  }
  if (window.rxjs) return Promise.resolve(window.rxjs);
  if (rxjsPromise) return rxjsPromise;

  const url = (import.meta.env.VITE_RXJS_URL as string | undefined)?.trim() || DEFAULT_URL;

  rxjsPromise = new Promise<RxJsRuntime>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-rxjs-shim="true"]`);
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.rxjs) resolve(window.rxjs);
        else reject(new Error('rxjs UMD loaded but window.rxjs is undefined'));
      });
      existing.addEventListener('error', () =>
        reject(new Error(`rxjs UMD failed to load from ${existing.src}`)),
      );
      if (window.rxjs) resolve(window.rxjs);
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = false;
    script.setAttribute('data-rxjs-shim', 'true');
    script.onload = (): void => {
      if (window.rxjs) resolve(window.rxjs);
      else reject(new Error(`rxjs UMD loaded but window.rxjs is undefined (${url})`));
    };
    script.onerror = (): void => reject(new Error(`rxjs UMD failed to load from ${url}`));
    document.head.appendChild(script);
  });
  return rxjsPromise;
}

/**
 * Synchronous accessor. Returns the loaded runtime or `null` if RxJS hasn't
 * finished loading yet. Callers should `await ensureRxJs()` first; this is
 * for tight loops that must not switch to async.
 */
export function getRxJs(): RxJsRuntime | null {
  return typeof window !== 'undefined' ? (window.rxjs ?? null) : null;
}
